import { Calendar, ClipboardList, Clock, LayoutDashboard, Users, UsersRound } from 'lucide-react';

/**
 * Primary sidebar navigation — the single source of truth shared by the Sidebar and
 * the post-login landing redirect (WRK-5474). Each item optionally declares the
 * `(action, subject)` ability required to see and reach it; the sidebar filters by it
 * and the landing logic sends the user to the first item they can access, so a role
 * without dashboard access (e.g. an Engineering/mobile user) never lands on a 403.
 */
export interface NavigationItem {
  name: string;
  path: string;
  icon: typeof LayoutDashboard;
  requiredAbility?: { action: string; subject: string };
}

export const primaryNavigation: NavigationItem[] = [
  {
    name: 'Dashboard',
    path: '/',
    icon: LayoutDashboard,
    requiredAbility: { action: 'read', subject: 'Report' },
  },
  {
    name: 'Jobs',
    path: '/jobs',
    icon: ClipboardList,
    requiredAbility: { action: 'read', subject: 'Job' },
  },
  {
    name: 'Schedule',
    path: '/schedule',
    icon: Calendar,
    requiredAbility: { action: 'read', subject: 'Planner' },
  },
  {
    // User reads are open to any authenticated user (WRK-5474), so no ability gate.
    name: 'Users',
    path: '/users',
    icon: Users,
  },
  {
    name: 'Teams',
    path: '/teams',
    icon: UsersRound,
    requiredAbility: { action: 'read', subject: 'Team' },
  },
  {
    name: 'Shifts',
    path: '/shifts',
    icon: Clock,
    requiredAbility: { action: 'read', subject: 'Shift' },
  },
];

/** Minimal ability shape the landing resolver needs — matches CASL's `ability.can`. */
interface AbilityLike {
  can: (action: string, subject: string) => boolean;
}

/**
 * The path a freshly-authenticated user should land on: the first primary nav item
 * their ability permits. Falls back to `/help` (an unguarded page) when the ability
 * permits none of them, so no role is dropped onto a guarded page it will 403 on.
 */
export function resolveLandingPath(ability: AbilityLike): string {
  const firstAllowed = primaryNavigation.find(
    (item) =>
      !item.requiredAbility ||
      ability.can(item.requiredAbility.action, item.requiredAbility.subject),
  );
  return firstAllowed?.path ?? '/help';
}
